/* Play (or sing) back a sequence of notes: via the mic note tracker or by tapping the
   on-screen fretboard / keyboard. Wrong notes say whether the target is higher or lower. */
import { useEffect, useRef, useState } from 'react';
import { audio } from '../../../core/audio';
import { trackNotes } from '../../../core/mic';
import { getSettings } from '../../../core/store';
import { type FretPos, mod, noteName, noteParts, positions, shortNote, shortPc } from '../../../core/theory';
import { Fretboard, type FretMark } from '../../../ui/Fretboard';
import { Keyboard, type KeyMarks } from '../../../ui/Keyboard';
import { emptySlots, Slots, type SlotView } from '../../../ui/widgets';
import type { NotesQuestion, QuestionApi } from '../types';

export function NotesArea({ q, api }: { q: NotesQuestion; api: QuestionApi }) {
  const n = q.target.length;
  const flex = q.flex != null ? q.flex : getSettings().octaveMode === 'flex';
  const keyboard = q.instrument === 'keyboard';
  const labelFor = (m: number): [string, string] => {
    const p = noteParts(m, { octave: !flex, flat: q.flat });
    return [p.main, p.sub];
  };
  const given = q.given || 0;
  const [slots, setSlots] = useState<SlotView[]>(() => {
    const s = emptySlots(n);
    for (let i = 0; i < given; i++) {
      const [main, sub] = labelFor(q.target[i] as number);
      s[i] = { main, sub, cls: 'ok reveal', shake: 0 };
    }
    return s;
  });
  const pos = useRef(given);
  const [cur, setCur] = useState(given);
  const [live, setLive] = useState(
    api.input === 'mic'
      ? q.voice
        ? 'Dinliyorum… Notaları sırayla söyle.'
        : 'Dinliyorum… Çalmaya başla.'
      : (q.screenHint ??
          (keyboard
            ? 'Duyduğun notaları klavyede sırayla bul.'
            : n === 1
              ? 'Duyduğun notayı sapta bul ve üzerine tıkla.'
              : 'Duyduğun notalara sapta sırayla tıkla.')),
  );
  const [showBoard, setShowBoard] = useState(api.input === 'screen');
  const [fretMarks, setFretMarks] = useState<FretMark[]>([]);
  /** A phone shows only part of the neck: the board scrolls to the first place the answer was shown at. */
  const [shownFret, setShownFret] = useState<number | undefined>(undefined);
  const [keyMarks, setKeyMarks] = useState<KeyMarks>({});
  const wrongTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handle = (m: number, meta?: FretPos) => {
    const p = pos.current;
    if (api.isDone() || p >= n) return;
    const exp = q.target[p] as number;
    const ok = q.match ? q.match(m, meta, p) : flex ? mod(m, 12) === mod(exp, 12) : m === exp;
    if (ok) {
      const [main, sub] = labelFor(exp);
      setSlots((s) => s.map((x, i) => (i === p ? { ...x, main, sub, cls: 'ok' } : x)));
      if (flex && m !== exp && api.input === 'mic') api.feedback('Doğru nota (farklı oktavda çaldın).', 'info');
      else api.feedback(null);
      pos.current = p + 1;
      setCur(p + 1);
      if (p + 1 >= n) api.done(true);
      return;
    }
    const [wrongMain] = labelFor(m);
    setSlots((s) => s.map((x, i) => (i === p ? { ...x, main: wrongMain, cls: 'wrong', shake: x.shake + 1 } : x)));
    clearTimeout(wrongTimer.current);
    wrongTimer.current = setTimeout(() => {
      setSlots((s) => s.map((x, i) => (i === p && x.cls === 'wrong' ? { ...x, main: '', cls: '' } : x)));
    }, 900);
    let txt = `Çaldığın: **${noteName(m)}**${meta && q.match ? ` (${meta.s}. tel, ${meta.f}. perde)` : ''}.`;
    if (q.match && meta && m === exp) txt = `Doğru nota ama başka bir telde (${meta.s}. tel). İstenen teli kullan.`;
    else if (q.direction !== false) {
      let d = exp - m;
      if (flex) {
        d = mod(exp, 12) - mod(m, 12);
        if (d > 6) d -= 12;
        if (d < -6) d += 12;
      }
      txt += d > 0 ? ' Hedef biraz daha **ince** (yukarıda).' : ' Hedef biraz daha **kalın** (aşağıda).';
    }
    api.feedback(txt, 'bad');
    api.miss({ played: m });
  };
  const handleRef = useRef(handle);
  handleRef.current = handle;

  // biome-ignore lint/correctness/useExhaustiveDependencies: registered once per mounted question
  useEffect(() => {
    api.setReveal(() => {
      const p = pos.current;
      if (p >= n) return null;
      const exp = q.target[p] as number;
      setShowBoard(true);
      if (keyboard) setKeyMarks({ [exp]: { cls: 'm-hint', label: shortNote(exp) } });
      else {
        const places = q.revealMarks || positions(exp, 12);
        setFretMarks(places.map((pp) => ({ s: pp.s, f: pp.f, cls: 'm-hint', label: shortPc(mod(exp, 12)) })));
        if (places.length) setShownFret(Math.min(...places.map((pp) => pp.f)));
      }
      return `Sıradaki nota: **${noteName(exp)}**${keyboard ? '' : ' (işaretli yerlerden birinde).'}`;
    });
    if (api.input !== 'mic') return;
    return trackNotes(q.voice ? 'voice' : 'guitar', (ev) => {
      setLive(`Duyulan: ${noteName(ev.midi)}`);
      handleRef.current(ev.midi);
    });
  }, [api]);

  useEffect(() => () => clearTimeout(wrongTimer.current), []);

  const pickable = api.input === 'screen';
  return (
    <>
      <Slots slots={slots} current={cur} />
      <div className="live-note" aria-live="polite">
        {live}
      </div>
      <div className="ex-board">
        {showBoard ? (
          keyboard ? (
            <Keyboard
              from={q.kb?.from ?? 48}
              to={q.kb?.to ?? 72}
              marks={keyMarks}
              onPick={
                pickable
                  ? (m) => {
                      audio.playNote(m, { dur: 0.7, inst: 'piano' });
                      handle(m);
                    }
                  : undefined
              }
            />
          ) : (
            <Fretboard
              from={q.fb?.from ?? 0}
              to={q.fb?.to ?? 12}
              marks={fretMarks}
              scrollToFret={shownFret ?? (q.fb && q.fb.from > 0 ? q.fb.from : undefined)}
              onPick={
                pickable
                  ? (s, f, m) => {
                      audio.playNote(m, { dur: 0.7, inst: 'guitar' });
                      handle(m, { s, f });
                    }
                  : undefined
              }
            />
          )
        ) : null}
      </div>
    </>
  );
}
